import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { view, utils, i18next } from '@yola/ws-sdk';
import bowser from 'yola-bowser';
import useFeatureFlags from 'yola-editor/src/js/modules/feature-flags/hooks/use-feature-flags';
import getBreakpointForCurrentDevice from 'src/js/modules/breakpoint-editing/helpers/get-breakpoint-for-current-device';
import {
  DESKTOP_BREAKPOINT,
  XL_BREAKPOINT_PX,
} from 'src/js/modules/breakpoint-editing/constants/breakpoints';
import { targetClasses } from 'src/js/modules/element-settings/constants/target-classes';
import highlighter from 'src/js/modules/highlighter';
import useHandlePosition from 'src/js/modules/extensions/common/hooks/use-handle-position';
import calculateOffsetTop from 'src/js/modules/extensions/common/helpers/calculate-offset-top';
import getEffectiveResponsiveClassValue from 'src/js/modules/extensions/common/helpers/get-effective-responsive-class-value';
import useDrag from 'src/js/modules/extensions/common/hooks/use-drag';
import ResizeHandle from 'src/js/modules/extensions/common/components/resize-handle';
import resizeToolPosition from 'src/js/modules/extensions/common/constants/resize-handle-positions';
import useEventListener from 'src/js/modules/utils/custom-hooks/use-event-listener';
import ResizeTooltip from 'src/js/modules/extensions/registry/ws-image/components/resize-tooltip';
import useEditorBreakpoint from 'src/js/modules/utils/custom-hooks/use-editor-breakpoint';
import getDefaultTraits from 'src/js/modules/analytics/segment/helpers/get-default-traits';
import isTouchDevice from 'src/js/modules/breakpoint-editing/verifiers/is-touch-device';
import highlighterOffset from 'src/js/modules/highlighter/helpers/highlighter-offset';
import segment from 'src/js/modules/analytics/segment';
import GridContainer from 'src/js/modules/extensions/registry/ws-column/containers/grid-container';
import getColumnsAlignment from '../helpers/get-column-alignment';
import convertNarrowerColFractionsToPercentages from '../helpers/convert-narrower-col-fractions-to-percentages';
import convertNarrowerColPercentagesToFractions from '../helpers/convert-narrower-col-percentages-to-fractions';
import hasWiderColPercentageClass from '../verifiers/has-wider-col-percentage-class';
import {
  FLEX_BASIS_PROPERTY,
  FLEX_GROW_PROPERTY,
  COLUMN_WIDTH_CUSTOM_CSS_PROPERTY,
  FREE_RESIZE_SNAP_THRESHOLD,
  COLUMNS_COUNT,
} from '../constants';

const {
  track,
  constants: { events },
} = segment;

const getCaptions = () => {
  const isMac = bowser.macOS || bowser.iOS;
  const captions = {
    cmdPressed: i18next.t('Release <b>Ctrl</b> to stick to the grid'),
    cmdReleased: i18next.t('Hold <b>Ctrl</b> for free resizing'),
    shiftPressed: i18next.t('Release <b>Shift</b> to resize this column only'),
    shiftReleased: i18next.t('Hold <b>Shift</b> to resize all columns'),
  };

  if (isMac) {
    captions.cmdPressed = i18next.t('Release <b>Cmd</b> to stick to the grid');
    captions.cmdReleased = i18next.t('Hold <b>Cmd</b> for free resizing');
  }

  return captions;
};

const DESKTOP_MIN_WIDTH = `${XL_BREAKPOINT_PX}px`;
const MIN_COLUMN_WIDTH_IN_PX = 1;
const GROW_FILL_VALUE = '1';
const GROW_FIXED_VALUE = '[0]';

const getFilledColumnIds = (columns, breakpoint) =>
  new Set(
    columns
      .filter(
        (column) =>
          getEffectiveResponsiveClassValue({
            element: column,
            className: targetClasses.GROW,
            breakpoint,
          }) === GROW_FILL_VALUE
      )
      .map((column) => view.accessors.getLiveElementId(column))
  );

const getClassWithFixedGrow = (currentClass, breakpoint) =>
  view.helpers.getPatchedResponsiveClass({
    currentClass: currentClass || '',
    className: targetClasses.GROW,
    value: GROW_FIXED_VALUE,
    breakpoint,
  });

const isColumnResizeEnabled = ({
  isBreakpointEditingEnabled,
  isDesktopViewport,
  isTouchableDevice,
}) => {
  if (isBreakpointEditingEnabled && isTouchableDevice) {
    return true;
  }
  return isDesktopViewport;
};

const isCommandPressed = (event) => {
  const { ctrlKey, metaKey } = event;
  if (bowser.macOS) {
    return metaKey;
  }
  return ctrlKey;
};

// Distance between adjacent column left edges in the column-gap grid
const getGridStep = (nominalColumnWidth, columnGap) =>
  nominalColumnWidth + columnGap / COLUMNS_COUNT;

const clampColumnIndex = (columnIndex) => Math.max(1, Math.min(COLUMNS_COUNT, columnIndex));

const getCompensatedWidth = (columnIndex, nominalColumnWidth, columnGap) => {
  const columnsFraction = clampColumnIndex(columnIndex) / COLUMNS_COUNT;
  const containerWidth = nominalColumnWidth * COLUMNS_COUNT;

  // Derived from ws-templates grid system styles to reproduce the same width calculation.
  return columnsFraction * containerWidth - columnGap * (1 - columnsFraction);
};

const getNearestGridColumn = (currentWidth, relativeOffset, nominalColumnWidth, columnGap) => {
  const gridStep = getGridStep(nominalColumnWidth, columnGap);
  const anchorGridIndex = Math.ceil(relativeOffset / gridStep);
  const offset = anchorGridIndex * gridStep - relativeOffset;
  const columnIndex = Math.round((currentWidth - offset + columnGap) / gridStep);

  return { columnIndex: clampColumnIndex(columnIndex), offset };
};

const getGridWidth = (currentWidth, nominalColumnWidth, columnGap) => {
  const { columnIndex } = getNearestGridColumn(currentWidth, 0, nominalColumnWidth, columnGap);

  return { width: getCompensatedWidth(columnIndex, nominalColumnWidth, columnGap), columnIndex };
};

const snapToGridWidth = (currentWidth, relativeOffset, nominalColumnWidth, columnGap, maxWidth) => {
  const { columnIndex, offset } = getNearestGridColumn(
    currentWidth,
    relativeOffset,
    nominalColumnWidth,
    columnGap
  );
  const nearestGridWidth = getCompensatedWidth(columnIndex, nominalColumnWidth, columnGap) + offset;

  if (Math.abs(currentWidth - nearestGridWidth) < FREE_RESIZE_SNAP_THRESHOLD) {
    return { width: nearestGridWidth, columnIndex: offset === 0 ? columnIndex : null };
  }

  return { width: Math.min(currentWidth, maxWidth), columnIndex: null };
};

const toNominalPercentage = (widthInPixels, containerWidth, columnGap) => {
  const widthRatio = (widthInPixels + columnGap) / (containerWidth + columnGap);

  return parseFloat((widthRatio * 100).toFixed(5));
};

const ColumnResizeTool = ({
  elementId,
  appearanceStyle,
  onActionStart,
  onActionEnd,
  onMouseEnter = utils.noop,
  onMouseLeave = utils.noop,
}) => {
  const [featureFlags] = useFeatureFlags(['breakpoint_editing']);
  const { breakpoint_editing: isBreakpointEditingEnabled } = featureFlags;
  const element = useMemo(() => view.accessors.getLiveElement(elementId), [elementId]);
  const liveDoc = view.accessors.getLiveDocument();
  const columnContainer = useMemo(() => element.parentElement, [element]);
  const columnGap = useMemo(() => {
    const computedStyle = liveDoc.defaultView.getComputedStyle(columnContainer);
    return parseInt(computedStyle.columnGap, 10);
  }, [columnContainer, liveDoc]);
  const columnContainerBounds = useMemo(
    () => columnContainer.getBoundingClientRect(),
    [columnContainer]
  );
  const breakpoint = isBreakpointEditingEnabled
    ? getBreakpointForCurrentDevice()
    : DESKTOP_BREAKPOINT;
  const columnContainerAlignment = useMemo(
    () => getColumnsAlignment(columnContainer),
    [columnContainer]
  );
  const allColumns = useMemo(() => Array.from(columnContainer.children), [columnContainer]);
  const isFillEnabled = useMemo(
    () =>
      // TODO: remove the `width="fill"` check after the attribute is migrated to grow classes.
      columnContainer.getAttribute('width') === 'fill' ||
      getFilledColumnIds(allColumns, breakpoint).size > 0,
    [allColumns, breakpoint, columnContainer]
  );
  const multiElementResizeAvailable = useMemo(() => allColumns.length > 1, [allColumns]);
  const [shiftPressed, setShiftStatus] = useState(false);
  const [commandPressed, setCommandStatus] = useState(false);
  const [tooltipPosition, showTooltip] = useState(null);
  const [isResizing, setResizingStatus] = useState(false);
  const leftHandleRef = useRef(null);
  const rightHandleRef = useRef(null);
  const filledColumnIdsRef = useRef(new Set());
  const currentRect = useRef(null);
  const activeDragHandlerPos = useRef(null);
  const columnWidth = useRef(null);
  const lastDistance = useRef([0, 0]);
  const scrollPosition = useSelector(view.selectors.getScrollPosition);
  const indent = highlighterOffset.get();
  const isDesktopViewport = useEditorBreakpoint(DESKTOP_MIN_WIDTH);
  const isTouchableDevice = isTouchDevice();
  const isResizeEnabled = isColumnResizeEnabled({
    isBreakpointEditingEnabled,
    isDesktopViewport,
    isTouchableDevice,
  });
  const captions = useMemo(getCaptions, []);

  const positions = useMemo(() => {
    switch (columnContainerAlignment) {
      case 'center':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: -indent,
          },
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: indent,
          },
        };
      case 'right':
        return {
          [resizeToolPosition.CENTER_LEFT]: {
            correctionX: -indent,
          },
        };

      default:
        return {
          [resizeToolPosition.CENTER_RIGHT]: {
            correctionX: indent,
          },
        };
    }
  }, [columnContainerAlignment, indent]);

  const [handlePosition, updateHandlePosition] = useHandlePosition({
    element,
    positions,
  });

  const showHighlighter = useCallback(
    (options = {}) => {
      const { withElementSize = false } = options;
      highlighter.operations.show([element], {
        forceUpdate: true,
        labelGap: 'medium',
        withElementSize,
      });
    },
    [element]
  );

  useEffect(() => {
    if (isResizeEnabled) {
      highlighter.operations.hide();
      showHighlighter();
    }

    return () => {
      highlighter.operations.hide();
    };
  }, [showHighlighter, isResizeEnabled]);

  const calculateNewWidth = useCallback(
    (distance) => {
      const {
        width: containerWidth,
        left: containerLeft,
        right: containerRight,
      } = columnContainerBounds;
      const { width: currentWidth, left: currentLeft, right: currentRight } = currentRect.current;
      let distanceWidth;
      let relativeOffset = null;

      switch (columnContainerAlignment) {
        case 'center':
          if (activeDragHandlerPos.current === 'left') {
            distanceWidth = -distance[0] * 2;
          } else {
            distanceWidth = distance[0] * 2;
          }
          break;
        case 'right':
          distanceWidth = -distance[0];
          relativeOffset = containerRight - currentRight;
          break;
        default:
          [distanceWidth] = distance;
          relativeOffset = currentLeft - containerLeft;
      }

      const newAbsoluteWidth = currentWidth + distanceWidth;
      let width = Math.min(newAbsoluteWidth, containerWidth);
      let columnIndex = null;

      if (!commandPressed) {
        ({ width, columnIndex } = getGridWidth(width, columnWidth.current, columnGap));
      } else if (relativeOffset !== null) {
        ({ width, columnIndex } = snapToGridWidth(
          width,
          relativeOffset,
          columnWidth.current,
          columnGap,
          containerWidth
        ));
      }

      return { width: Math.max(MIN_COLUMN_WIDTH_IN_PX, width), columnIndex };
    },
    [columnContainerAlignment, commandPressed, columnContainerBounds, columnGap]
  );

  const handleDrag = useCallback(
    (distance) => {
      const { width: widthInPixels } = calculateNewWidth(distance);
      const elements = shiftPressed ? allColumns : [element];

      elements.forEach((column) => {
        column.style.setProperty(FLEX_BASIS_PROPERTY, `${widthInPixels}px`);
        column.style.setProperty(COLUMN_WIDTH_CUSTOM_CSS_PROPERTY, `${widthInPixels}px`);
        column.style.setProperty(FLEX_GROW_PROPERTY, '0');
      });

      updateHandlePosition();
      lastDistance.current = distance;
    },
    [allColumns, element, shiftPressed, updateHandlePosition, calculateNewWidth]
  );

  const handleDragEnd = (distance) => {
    const { width: widthInPixels, columnIndex } = calculateNewWidth(distance);
    const { width: containerWidth } = columnContainerBounds;
    const nominalPercentage = toNominalPercentage(widthInPixels, containerWidth, columnGap);
    const targetColumns = shiftPressed ? allColumns : [element];
    const operations = targetColumns.map((column) => {
      const targetColumnElementId = view.accessors.getLiveElementId(column);
      const currentClass = view.accessors.getElementAttribute(targetColumnElementId, 'class');
      const wasFilled = filledColumnIdsRef.current.has(targetColumnElementId);
      const classWithFixedGrow = wasFilled
        ? getClassWithFixedGrow(currentClass, breakpoint)
        : currentClass;
      // Fractional `col-[N/12]` values on narrower breakpoints should be converted to percentages.
      // Fraction CSS lives in `ws-templates`, `col-[X%]` - generated  CSS and injected separately, lower in `<head>`, so it wins.
      // `@media` does not add specificity, so source order decides: a wider-breakpoint percentage would also win on narrower ones and replace those fraction widths.
      const shouldUseFraction =
        columnIndex !== null && !hasWiderColPercentageClass(classWithFixedGrow, breakpoint);
      const colValue = shouldUseFraction
        ? `[${columnIndex}/${COLUMNS_COUNT}]`
        : `[${nominalPercentage}%]`;
      const patchedClass = shouldUseFraction
        ? convertNarrowerColPercentagesToFractions(classWithFixedGrow, breakpoint)
        : convertNarrowerColFractionsToPercentages(classWithFixedGrow, breakpoint);

      return [
        view.operations.setElementAttribute,
        [
          targetColumnElementId,
          'class',
          view.helpers.getPatchedResponsiveClass({
            currentClass: patchedClass,
            className: targetClasses.COL,
            value: colValue,
            breakpoint,
          }),
        ],
      ];
    });

    if (isFillEnabled) {
      const columnElementId = view.accessors.getLiveElementId(columnContainer);
      operations.push([view.operations.removeElementAttribute, [columnElementId, 'width']]);
    }

    view.operations.bulkViewOperations(operations);

    allColumns.forEach((column) => {
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
      column.style.removeProperty(COLUMN_WIDTH_CUSTOM_CSS_PROPERTY);
      column.style.removeProperty(FLEX_GROW_PROPERTY);
    });

    const oldWidth = Math.round(currentRect.current.width);
    const newWidth = Math.round(widthInPixels);

    return {
      newWidth: `${newWidth}px`,
      oldWidth: `${oldWidth}px`,
    };
  };

  const resetColumnsWidth = useCallback(() => {
    allColumns.forEach((column) => {
      if (column === element) return;
      column.style.removeProperty(FLEX_BASIS_PROPERTY);
      column.style.removeProperty(COLUMN_WIDTH_CUSTOM_CSS_PROPERTY);
      column.style.removeProperty(FLEX_GROW_PROPERTY);
    });
  }, [allColumns, element]);

  const handleMouseEnter = (e) => {
    const { target } = e;
    onMouseEnter();

    let position = resizeToolPosition.CENTER_RIGHT;
    if (target === leftHandleRef.current) {
      position = resizeToolPosition.CENTER_LEFT;
    }

    if (!isResizing) {
      showTooltip(position);
    }
  };

  const handleMouseLeave = () => {
    onMouseLeave();
    if (!isResizing) {
      showTooltip(null);
    }
  };

  useDrag(
    [leftHandleRef, rightHandleRef],
    {
      onStart(event) {
        activeDragHandlerPos.current = event.target === leftHandleRef.current ? 'left' : 'right';
        showHighlighter({ isGroup: shiftPressed, withElementSize: true });
        currentRect.current = element.getBoundingClientRect();
        columnWidth.current = columnContainerBounds.width / COLUMNS_COUNT;
        // Reset the distance from the previous drag session, otherwise the effect
        // below re-applies it to the current width on a simple handle click
        lastDistance.current = [0, 0];
        setResizingStatus(true);
        showTooltip(null);
        filledColumnIdsRef.current = getFilledColumnIds(allColumns, breakpoint);

        if (isFillEnabled) {
          columnContainer.removeAttribute('width');
        }

        onActionStart();

        track(events.COLUMN_RESIZING_INITIATED, {
          ...getDefaultTraits(elementId),
        });
      },

      onMove(distance) {
        handleDrag(distance);
        showHighlighter({ withElementSize: true });
      },

      onEnd(distance) {
        setResizingStatus(false);
        const { newWidth, oldWidth } = handleDragEnd(distance, true);

        onActionEnd();
        if (shiftPressed) {
          const otherColumns = allColumns.filter((column) => column !== element);
          highlighter.operations.hide(otherColumns);
        }
        track(events.COLUMN_RESIZING_COMPLETED, {
          ...getDefaultTraits(elementId),
          affectedColumns: shiftPressed ? allColumns.length : 1,
          newWidth,
          oldWidth,
          shiftKeyPressed: shiftPressed,
        });
      },
    },
    { throttleMove: true }
  );

  useEffect(() => {
    if (isResizing) {
      handleDrag(lastDistance.current);

      if (shiftPressed) {
        highlighter.operations.show(allColumns, {
          isGroup: true,
          withElementSize: true,
        });
      } else {
        resetColumnsWidth();
        const otherColumns = allColumns.filter((column) => column !== element);
        highlighter.operations.hide(otherColumns);
        updateHandlePosition();
      }
    }
  }, [
    shiftPressed,
    commandPressed,
    isResizing,
    handleDrag,
    resetColumnsWidth,
    updateHandlePosition,
    allColumns,
    element,
  ]);

  const handleKey = ({ shiftKey, ctrlKey, metaKey }) => {
    const cmdPressed = isCommandPressed({ ctrlKey, metaKey });
    const multiColumnShift = multiElementResizeAvailable && shiftKey;

    if (cmdPressed === commandPressed && multiColumnShift === shiftPressed) return;

    setCommandStatus(cmdPressed);
    setShiftStatus(multiColumnShift);
  };

  useEventListener('keydown', handleKey, document);
  useEventListener('keyup', handleKey, document);
  useEventListener('keydown', handleKey, element.ownerDocument);
  useEventListener('keyup', handleKey, element.ownerDocument);

  if (
    !isResizeEnabled ||
    (!handlePosition[resizeToolPosition.CENTER_LEFT] &&
      !handlePosition[resizeToolPosition.CENTER_RIGHT])
  ) {
    return null;
  }

  const leftPosition = handlePosition[resizeToolPosition.CENTER_LEFT];
  const rightPosition = handlePosition[resizeToolPosition.CENTER_RIGHT];
  const pos = leftPosition || rightPosition;

  const offtop = calculateOffsetTop(element, pos[1], { align: 'center' });

  return (
    <React.Fragment>
      {columnContainerAlignment !== 'left' && (
        <ResizeHandle
          ref={leftHandleRef}
          left={leftPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}
      {columnContainerAlignment !== 'right' && (
        <ResizeHandle
          ref={rightHandleRef}
          left={rightPosition[0]}
          top={offtop}
          shape="oval"
          direction="left-right"
          appearanceStyle={appearanceStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      )}

      {isResizing && (
        <GridContainer
          scrollPosition={scrollPosition}
          containerBounds={columnContainerBounds}
          columnGap={columnGap}
          columnsCount={COLUMNS_COUNT}
        />
      )}

      {tooltipPosition && (
        <ResizeTooltip
          left={handlePosition[tooltipPosition][0]}
          top={handlePosition[tooltipPosition][1]}
          direction="down"
        >
          {multiElementResizeAvailable && (
            <div
              // eslint-disable-next-line yola/react/no-danger
              dangerouslySetInnerHTML={{
                __html: shiftPressed ? captions.shiftPressed : captions.shiftReleased,
              }}
            />
          )}
          <div
            // eslint-disable-next-line yola/react/no-danger
            dangerouslySetInnerHTML={{
              __html: commandPressed ? captions.cmdPressed : captions.cmdReleased,
            }}
          />
        </ResizeTooltip>
      )}
    </React.Fragment>
  );
};

ColumnResizeTool.propTypes = {
  elementId: PropTypes.string.isRequired,
  appearanceStyle: PropTypes.shape().isRequired,
  onActionStart: PropTypes.func.isRequired,
  onActionEnd: PropTypes.func.isRequired,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
};

export default ColumnResizeTool;
